import { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useDiagnosticStore } from '@shared/state';
import {
  startAnchoredDiagnostic,
  submitAnchoredAnswer,
  disputeDimension,
  classifyLearnerNote,
  type AnchoredTurn,
  type AnchoredProgress,
} from '@shared/services/diagnostic';
import { generateProfileSummary } from '@shared/services/profile-summary';
import { useAuthStore } from '@shared/state';
import type { DiagnosticMessage, DiagnosticConversationPayload } from '@shared/types/diagnostic';
import type { CogniPrintProfile, DiagnosticDimensionKey } from '@shared/types/cogniprint';
import { ThinkingIndicator } from './ThinkingIndicator';
import { doc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';

interface DiagnosticConversationProps {
  /**
   * Fired every time a complete, validated profile exists — first at the end of
   * the main sequence, then again after each dispute re-score or summary rewrite.
   * NOTHING IS SAVED HERE. The page owns Save; the learner presses it.
   */
  onReportReady: (profile: CogniPrintProfile) => void;
  /** Side panel next to the report (after completion) or the main window (during). */
  variant?: 'main' | 'side';
}

export function DiagnosticConversation({ onReportReady, variant = 'main' }: DiagnosticConversationProps) {
  const user = useAuthStore((s) => s.user);
  const store = useDiagnosticStore();
  const [thinking, setThinking] = useState(false);
  const reportMode = store.profile !== null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionId = useRef(store.sessionId ?? `diag_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [store.messages, thinking, scrollToBottom]);

  // Open the session document once.
  useEffect(() => {
    if (!user || store.sessionId) return;
    store.setSessionId(sessionId.current);
    setDoc(doc(db, 'diagnosticSessions', sessionId.current), {
      sessionId: sessionId.current,
      userId: user.uid,
      userEmail: user.email,
      mode: store.mode,
      startedAt: new Date().toISOString(),
      messages: [],
      status: 'in_progress',
      profile: null,
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveMessage(msg: DiagnosticMessage) {
    try {
      await updateDoc(doc(db, 'diagnosticSessions', sessionId.current), {
        messages: arrayUnion({ role: msg.role, content: msg.content, timestamp: msg.timestamp }),
      });
    } catch {
      // Session doc might not exist yet — ignore
    }
  }

  function buildPayload(): DiagnosticConversationPayload {
    return {
      mode: store.mode,
      conversationHistory: store.messages.map((m) => ({ role: m.role, content: m.content })),
      userId: user!.uid,
      userDisplayName: user!.displayName || 'learner',
      deviceType: window.innerWidth < 768 ? 'mobile' : 'desktop',
      subscriptionTier: user!.subscription as 'free' | 'learner' | 'pro' | 'teams',
    };
  }

  function progressFromStore(): AnchoredProgress {
    const s = useDiagnosticStore.getState();
    return {
      mode: s.mode,
      responses: s.itemResponses,
      followedUp: s.followedUp,
      activeItemId: s.activeItemId,
      disputed: s.disputed,
    };
  }

  // Start the diagnostic on mount.
  useEffect(() => {
    if (store.status !== 'in_progress' || store.messages.length > 0 || !user) return;
    setThinking(true);
    startAnchoredDiagnostic(buildPayload())
      .then((turn) => {
        store.addMessage(turn.message);
        saveMessage(turn.message);
        if (turn.kind === 'item') store.setCurrentItem(turn.itemId);
      })
      .catch((err) => store.setError(err instanceof Error ? err.message : 'Failed to start diagnostic'))
      .finally(() => setThinking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.status]);

  /** A complete turn: score is already computed by the app. Narrate, then hand up. */
  async function publishProfile(profile: CogniPrintProfile, learnerNote?: string) {
    store.setStatus('computing');
    let summary = profile.summary;
    try {
      summary = await generateProfileSummary(profile, user!.displayName || 'learner', learnerNote);
    } catch {
      // Narration is a nice-to-have; the scored profile stands.
    }
    const withSummary = { ...profile, summary };
    store.setProfile(withSummary);
    onReportReady(withSummary);
    // Keep the chat live for disputes and rewrites.
    store.setStatus('in_progress');
  }

  async function applyTurn(turn: AnchoredTurn, progress: AnchoredProgress) {
    store.setProgress({
      itemResponses: progress.responses,
      followedUp: progress.followedUp ?? [],
      activeItemId: progress.activeItemId ?? null,
      disputed: progress.disputed ?? [],
    });
    store.addMessage(turn.message);
    saveMessage(turn.message);

    if (turn.kind === 'item' || turn.kind === 'follow_up') {
      store.setCurrentItem(turn.itemId);
      if (turn.kind === 'item') {
        store.updateProgress(
          turn.index,
          Math.min(Math.round((turn.index / turn.total) * store.totalDimensions), store.totalDimensions),
        );
      }
      return;
    }

    store.setCurrentItem(null);

    if (turn.kind === 'invalid') {
      console.error('[diagnostic] rejected an untrusted profile', turn.reasons);
      void trackEvent({
        name: ANALYTICS_EVENTS.DIAGNOSTIC_PROFILE_INVALID,
        properties: {
          ...getBaseProperties(),
          diagnosticType: store.mode,
          reasons: turn.reasons,
          questionsAnswered: progress.responses.filter((r) => !r.skipped).length,
        },
      }).catch(() => {});
      store.setError(
        "I couldn't build a profile from that — too much of it went unanswered. Let's start again when you have a couple of minutes.",
      );
      return;
    }

    store.updateProgress(progress.responses.length, store.totalDimensions);
    await publishProfile(turn.profile);
  }

  // The report UI asked for a dispute on a specific dimension.
  useEffect(() => {
    const pending = store.pendingDispute;
    if (!pending || thinking || !user) return;
    store.clearPendingDispute();
    void runDispute(pending.dimension, pending.words);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.pendingDispute]);

  async function runDispute(dimension: DiagnosticDimensionKey, words: string) {
    setThinking(true);
    try {
      const result = await disputeDimension(buildPayload(), progressFromStore(), dimension, words);
      if (!result) {
        const msg: DiagnosticMessage = {
          id: `system_${Date.now()}`, role: 'assistant', timestamp: new Date().toISOString(),
          content: "I've already asked you an extra question about that one, and I score from answers rather than adjusting by hand — so that result stands on what you've told me. If the wording of the write-up is what's off, tell me and I'll rewrite it.",
        };
        store.addMessage(msg);
        return;
      }
      await applyTurn(result.turn, result.progress);
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setThinking(false);
    }
  }

  async function handleSelectOption(option: string, optionLabel: string) {
    if (thinking) return;

    const userMsg: DiagnosticMessage = {
      id: `user_${Date.now()}`, role: 'user', content: optionLabel,
      timestamp: new Date().toISOString(), selectedOption: option,
    };
    store.addMessage(userMsg);
    saveMessage(userMsg);
    setThinking(true);

    try {
      const progress = progressFromStore();
      const answeringAnItem = !!store.currentItemId;

      // Typed feedback on the finished report, with no item on screen: route it.
      // About one dimension → ask its reserve question. About the wording → rewrite.
      // Those are the only two things a learner can change; neither sets a number.
      if (reportMode && !answeringAnItem) {
        const target = await classifyLearnerNote(buildPayload(), optionLabel);
        if (target === 'summary') {
          await publishProfile(store.profile!, optionLabel);
          const msg: DiagnosticMessage = {
            id: `system_${Date.now()}`, role: 'assistant', timestamp: new Date().toISOString(),
            content: "I've reworded the summary with that in mind — same results underneath, told differently. Take another look.",
          };
          store.addMessage(msg);
        } else {
          await runDispute(target, optionLabel);
        }
        return;
      }

      const { turn, progress: nextProgress } = await submitAnchoredAnswer(
        buildPayload(),
        progress,
        { optionId: option, text: optionLabel },
      );
      await applyTurn(turn, nextProgress);
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setThinking(false);
    }
  }

  const lastMessage = store.messages[store.messages.length - 1];
  const showOptions = !thinking && lastMessage?.role === 'assistant' && !!lastMessage.options?.length;
  const showTextInput = !thinking && lastMessage?.role === 'assistant' && !lastMessage.options?.length;
  const dense = variant === 'side';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div ref={scrollRef} className={`flex-1 overflow-y-auto ${dense ? 'px-4 py-4 space-y-3' : 'px-5 py-5 space-y-4'}`}>
        {store.messages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2.5`}
          >
            {msg.role === 'assistant' && (
              <img src="/CogHead.png" alt="Professor Cogni" className={`${dense ? 'w-7 h-7' : 'w-9 h-9'} object-contain shrink-0 mt-1`} />
            )}
            <div
              className={`max-w-[90%] ${dense ? 'px-4 py-2.5' : 'px-5 py-3'} rounded-2xl text-sm font-body leading-relaxed ${
                msg.role === 'user' ? 'bg-indigo-500/15 text-content rounded-br-md' : 'bg-dark-subtle text-content-secondary rounded-bl-md'
              }`}
            >
              {msg.role === 'user' ? (
                <p>{msg.content}</p>
              ) : (
                <div className="prose-cogniprint text-sm [&>p]:mb-3 [&>p:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                    {msg.content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {thinking && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start gap-2.5">
            <motion.img
              src="/CogHead.png" alt="Professor Cogni" className={`${dense ? 'w-7 h-7' : 'w-9 h-9'} object-contain shrink-0 mt-1`}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <div className="bg-dark-subtle rounded-2xl rounded-bl-md">
              <ThinkingIndicator />
            </div>
          </motion.div>
        )}
      </div>

      <div className={`shrink-0 border-t border-dark-border bg-dark-surface ${dense ? 'px-4 py-3' : 'px-5 py-4'}`}>
        {showOptions && (
          <AnimatePresence>
            <div className="space-y-2">
              {lastMessage.options!.map((opt, i) => (
                <motion.button
                  key={opt.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => handleSelectOption(opt.id, opt.label)}
                  className="w-full text-left px-4 py-3 rounded-lg bg-dark-subtle border border-dark-border
                    text-sm text-content font-body
                    hover:border-indigo-500/50 hover:bg-dark-hover active:bg-indigo-500/10
                    transition-colors cursor-pointer"
                >
                  {opt.label}
                </motion.button>
              ))}
            </div>
          </AnimatePresence>
        )}

        {showTextInput && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const input = (e.target as HTMLFormElement).elements.namedItem('answer') as HTMLInputElement;
              const value = input?.value?.trim();
              if (value) {
                handleSelectOption(value, value);
                input.value = '';
              }
            }}
            className="flex gap-2"
          >
            <input
              name="answer"
              type="text"
              placeholder={reportMode ? "Tell Professor Cogni what doesn't feel right…" : 'Type your answer…'}
              autoFocus
              className="flex-1 px-4 py-3 rounded-lg bg-dark-subtle border border-dark-border
                text-sm text-content font-body
                focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500
                placeholder:text-content-dim"
            />
            <button type="submit" className="px-4 py-3 rounded-lg bg-indigo-500 text-white hover:bg-[#7E70EF] transition-colors cursor-pointer">
              <Send size={16} />
            </button>
          </form>
        )}

        {thinking && (
          <p className="text-xs text-content-dim font-body text-center">Professor Cogni is thinking…</p>
        )}

        {store.error && (
          <div className="text-center">
            <p className="text-error text-xs font-body mb-2">{store.error}</p>
            <button onClick={() => store.setError(null)} className="text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}
